import { useState } from 'react';

interface ConstructionDocsProps {
  floorPlanConfig?: {
    bedrooms: number;
    bathrooms: number;
    area: number;
    unit: string;
    kitchenType: string;
    extras: string[];
  };
  darkMode: boolean;
}

export default function ConstructionDocs({ floorPlanConfig: rawConfig, darkMode }: ConstructionDocsProps) {
  const floorPlanConfig = rawConfig ?? {
    bedrooms: 3,
    bathrooms: 2,
    area: 2000,
    unit: 'sqft',
    kitchenType: 'open',
    extras: [] as string[],
  };

  // Derived helpers replacing old boolean fields
  const isMetric = floorPlanConfig.unit === 'sqm';
  const isOpenKitchen = floorPlanConfig.kitchenType === 'open';

  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState(0);
  const [isGenerated, setIsGenerated] = useState(false);
  const [currency, setCurrency] = useState<'USD' | 'EUR' | 'GBP'>('USD');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'info'>('success');
  const [isExporting, setIsExporting] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  const generationSteps = [
    'Analyzing floor plan',
    'Calculating room areas',
    'Estimating materials',
    'Computing costs',
    'Compiling report'
  ];

  const currencySymbols = { USD: '$', EUR: '€', GBP: '£' };
  const sym = currencySymbols[currency];

  const showToastMsg = (msg: string, type: 'success' | 'info' = 'success') => {
    setToastMessage(msg);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3500);
  };

  const handleGenerate = () => {
    setIsGenerating(true);
    setGenerationStep(0);
    setIsGenerated(false);
    const interval = setInterval(() => {
      setGenerationStep((prev) => {
        if (prev >= generationSteps.length - 1) {
          clearInterval(interval);
          setIsGenerating(false);
          setIsGenerated(true);
          return prev;
        }
        return prev + 1;
      });
    }, 800);
  };

  const handleRecalculate = () => handleGenerate();

  const handleShare = async () => {
    setIsSharing(true);
    const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const lines: string[] = [
      '🏗️  MASTER ARCHITECT — Construction Documents Summary',
      `📅  Generated: ${date}`,
      '',
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      '📋  PROJECT SUMMARY',
      `  • Bedrooms:    ${floorPlanConfig.bedrooms}`,
      `  • Bathrooms:   ${floorPlanConfig.bathrooms}`,
      `  • Total Area:  ${floorPlanConfig.area} ${isMetric ? 'm²' : 'sqft'}`,
      `  • Kitchen Type: ${isOpenKitchen ? 'Open Plan' : 'Closed'}`,
      `  • Extras:      ${floorPlanConfig.extras.length > 0 ? floorPlanConfig.extras.join(', ') : 'None'}`,
      '',
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      '🏠  ROOM SCHEDULE',
      ...roomSchedule.map(r => `  • ${r.name.padEnd(22)} ${r.area} m²  (${r.dimensions})`),
      '',
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      '💰  COST ESTIMATE BREAKDOWN',
      ...costBreakdown.map(s => {
        const sub = s.items.reduce((a, i) => a + i.cost, 0);
        return `  • ${s.category.padEnd(22)} ${sym}${sub.toLocaleString()}`;
      }),
      '',
      `  ▶  GRAND TOTAL:  ${sym}${totalCost.toLocaleString()}  (${budgetTier.label} tier)`,
      '',
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      '📌  Shared via Master Architect  |  masterarchitect.app',
    ];
    const summary = lines.join('\n');
    try {
      await navigator.clipboard.writeText(summary);
      showToastMsg('Summary copied to clipboard! Paste it anywhere to share.', 'success');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = summary;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      showToastMsg('Summary copied to clipboard!', 'success');
    } finally {
      setIsSharing(false);
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    showToastMsg('Generating PDF...', 'info');
    try {
      const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Construction Documents – Master Architect</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 11px; color: #1a1a1a; background: #fff; padding: 24px; }
            .header { display: flex; align-items: center; justify-content: space-between; padding-bottom: 14px; border-bottom: 3px solid #14b8a6; margin-bottom: 20px; }
            .header-left h1 { font-size: 20px; font-weight: 800; color: #14b8a6; }
            .header-left p { font-size: 11px; color: #666; margin-top: 2px; }
            .header-right { text-align: right; font-size: 10px; color: #888; }
            .section { margin-bottom: 22px; page-break-inside: avoid; }
            .section-title { font-size: 13px; font-weight: 700; color: #14b8a6; border-bottom: 1px solid #e5e7eb; padding-bottom: 5px; margin-bottom: 10px; }
            .summary-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; margin-bottom: 4px; }
            .summary-item { background: #f0fdfa; border: 1px solid #99f6e4; border-radius: 6px; padding: 8px 10px; }
            .summary-item .label { font-size: 9px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; }
            .summary-item .value { font-size: 12px; font-weight: 700; color: #0f766e; margin-top: 2px; }
            table { width: 100%; border-collapse: collapse; font-size: 10px; margin-bottom: 4px; }
            th { background: #14b8a6; color: #fff; font-weight: 700; padding: 6px 8px; text-align: left; }
            td { padding: 5px 8px; border-bottom: 1px solid #f3f4f6; }
            tr:nth-child(even) td { background: #f0fdfa; }
            .cost-row { display: flex; justify-content: space-between; padding: 3px 0; font-size: 10px; }
            .cost-section { margin-bottom: 10px; }
            .cost-section-title { font-weight: 700; font-size: 11px; margin-bottom: 4px; color: #374151; }
            .cost-subtotal { display: flex; justify-content: space-between; padding: 4px 0; font-weight: 700; border-top: 1px solid #e5e7eb; margin-top: 3px; font-size: 10px; }
            .grand-total { display: flex; justify-content: space-between; padding: 10px 12px; background: #14b8a6; color: #fff; border-radius: 8px; font-size: 14px; font-weight: 800; margin-top: 12px; }
            .spec-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
            .spec-item .spec-label { font-size: 9px; color: #6b7280; }
            .spec-item .spec-value { font-size: 11px; font-weight: 600; color: #111827; margin-top: 2px; }
            .footer { margin-top: 28px; padding-top: 10px; border-top: 1px solid #e5e7eb; display: flex; justify-content: space-between; font-size: 9px; color: #9ca3af; }
            @media print { body { padding: 12px; } @page { margin: 12mm; size: A4; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="header-left"><h1>Master Architect</h1><p>Construction Documents Report</p></div>
            <div class="header-right">
              <div>Generated: ${date}</div>
              <div style="margin-top:4px;">Currency: ${currency}</div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Project Summary</div>
            <div class="summary-grid">
              <div class="summary-item"><div class="label">Bedrooms</div><div class="value">${floorPlanConfig.bedrooms}</div></div>
              <div class="summary-item"><div class="label">Bathrooms</div><div class="value">${floorPlanConfig.bathrooms}</div></div>
              <div class="summary-item"><div class="label">Total Area</div><div class="value">${floorPlanConfig.area} ${isMetric ? 'm²' : 'sqft'}</div></div>
              <div class="summary-item"><div class="label">Kitchen Type</div><div class="value">${isOpenKitchen ? 'Open Plan' : 'Closed'}</div></div>
              <div class="summary-item" style="grid-column: span 2;"><div class="label">Extras</div><div class="value">${floorPlanConfig.extras.length > 0 ? floorPlanConfig.extras.join(', ') : 'None'}</div></div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Room Schedule</div>
            <table>
              <thead><tr><th>Room Name</th><th>Area (m²)</th><th>Dimensions</th><th>Flooring</th><th>Ceiling Ht.</th><th>Notes</th></tr></thead>
              <tbody>${roomSchedule.map(r => `<tr><td><strong>${r.name}</strong></td><td>${r.area}</td><td>${r.dimensions}</td><td>${r.flooring}</td><td>${r.ceiling}</td><td>${r.notes}</td></tr>`).join('')}</tbody>
            </table>
          </div>

          <div class="section">
            <div class="section-title">Material List</div>
            <table>
              <thead><tr><th>Material</th><th>Category</th><th>Qty</th><th>Unit</th><th>Est. Cost (${sym})</th></tr></thead>
              <tbody>${materialList.map(m => `<tr><td><strong>${m.material}</strong></td><td>${m.category}</td><td>${m.quantity}</td><td>${m.unit}</td><td>${sym}${m.cost.toLocaleString()}</td></tr>`).join('')}</tbody>
            </table>
          </div>

          <div class="section">
            <div class="section-title">Cost Estimate Summary</div>
            ${costBreakdown.map(s => `
              <div class="cost-section">
                <div class="cost-section-title">${s.category}</div>
                ${s.items.map(i => `<div class="cost-row"><span>${i.name}</span><span>${sym}${i.cost.toLocaleString()}</span></div>`).join('')}
                <div class="cost-subtotal"><span>Subtotal</span><span>${sym}${s.items.reduce((a, i) => a + i.cost, 0).toLocaleString()}</span></div>
              </div>
            `).join('')}
            <div class="grand-total"><span>Grand Total</span><span>${sym}${totalCost.toLocaleString()}</span></div>
          </div>

          <div class="section">
            <div class="section-title">Specification Sheet</div>
            <div class="spec-grid">
              ${[
                ['Wall Thickness', '200mm (8")'],
                ['Insulation Type', 'Mineral Wool R-30'],
                ['Window Specs', 'Double Glazed, Low-E'],
                ['Door Specs', 'Solid Core, Fire Rated'],
                ['Foundation Type', 'Reinforced Concrete Slab'],
                ['Roof Type', 'Pitched, Clay Tiles'],
              ].map(([label, value]) => `<div class="spec-item"><div class="spec-label">${label}</div><div class="spec-value">${value}</div></div>`).join('')}
            </div>
          </div>

          <div class="footer">
            <span>Master Architect · Construction Documents</span>
            <span>Confidential – For internal use only</span>
          </div>
        </body>
        </html>
      `;
      const printWindow = window.open('', '_blank', 'width=900,height=700');
      if (!printWindow) {
        showToastMsg('Please allow popups to export PDF.');
        return;
      }
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
        showToastMsg('PDF exported successfully!');
      }, 500);
    } catch {
      showToastMsg('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExport = (type: string) => {
    showToastMsg(`${type} downloaded successfully!`);
  };

  const handlePrint = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Construction Documents – Master Architect</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 11px; color: #1a1a1a; background: #fff; padding: 24px; }
          .header { display: flex; align-items: center; justify-content: space-between; padding-bottom: 14px; border-bottom: 3px solid #14b8a6; margin-bottom: 20px; }
          .header-left h1 { font-size: 20px; font-weight: 800; color: #14b8a6; }
          .header-left p { font-size: 11px; color: #666; margin-top: 2px; }
          .header-right { text-align: right; font-size: 10px; color: #888; }
          .section { margin-bottom: 22px; }
          .section-title { font-size: 13px; font-weight: 700; color: #14b8a6; border-bottom: 1px solid #e5e7eb; padding-bottom: 5px; margin-bottom: 10px; }
          .summary-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; margin-bottom: 4px; }
          .summary-item { background: #f0fdfa; border: 1px solid #99f6e4; border-radius: 6px; padding: 8px 10px; }
          .summary-item .label { font-size: 9px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; }
          .summary-item .value { font-size: 12px; font-weight: 700; color: #0f766e; margin-top: 2px; }
          table { width: 100%; border-collapse: collapse; font-size: 10px; }
          th { background: #14b8a6; color: #fff; font-weight: 700; padding: 6px 8px; text-align: left; }
          td { padding: 5px 8px; border-bottom: 1px solid #f3f4f6; }
          tr:nth-child(even) td { background: #f0fdfa; }
          .cost-row { display: flex; justify-content: space-between; padding: 3px 0; font-size: 10px; }
          .cost-section { margin-bottom: 10px; }
          .cost-section-title { font-weight: 700; font-size: 11px; margin-bottom: 4px; color: #374151; }
          .cost-subtotal { display: flex; justify-content: space-between; padding: 4px 0; font-weight: 700; border-top: 1px solid #e5e7eb; margin-top: 3px; font-size: 10px; }
          .grand-total { display: flex; justify-content: space-between; padding: 10px 12px; background: #14b8a6; color: #fff; border-radius: 8px; font-size: 14px; font-weight: 800; margin-top: 12px; }
          .spec-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
          .spec-item .spec-label { font-size: 9px; color: #6b7280; }
          .spec-item .spec-value { font-size: 11px; font-weight: 600; color: #111827; margin-top: 2px; }
          .footer { margin-top: 28px; padding-top: 10px; border-top: 1px solid #e5e7eb; display: flex; justify-content: space-between; font-size: 9px; color: #9ca3af; }
          @media print { body { padding: 12px; } @page { margin: 12mm; size: A4; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-left"><h1>Master Architect</h1><p>Construction Documents Report</p></div>
          <div class="header-right">
            <div>Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            <div style="margin-top:4px;">Currency: ${currency}</div>
          </div>
        </div>
        <div class="section">
          <div class="section-title">Project Summary</div>
          <div class="summary-grid">
            <div class="summary-item"><div class="label">Bedrooms</div><div class="value">${floorPlanConfig.bedrooms}</div></div>
            <div class="summary-item"><div class="label">Bathrooms</div><div class="value">${floorPlanConfig.bathrooms}</div></div>
            <div class="summary-item"><div class="label">Total Area</div><div class="value">${floorPlanConfig.area} ${isMetric ? 'm²' : 'sqft'}</div></div>
            <div class="summary-item"><div class="label">Kitchen Type</div><div class="value">${isOpenKitchen ? 'Open Plan' : 'Closed'}</div></div>
            <div class="summary-item" style="grid-column: span 2;"><div class="label">Extras</div><div class="value">${floorPlanConfig.extras.length > 0 ? floorPlanConfig.extras.join(', ') : 'None'}</div></div>
          </div>
        </div>
        <div class="section">
          <div class="section-title">Room Schedule</div>
          <table>
            <thead><tr><th>Room Name</th><th>Area (m²)</th><th>Dimensions</th><th>Flooring</th><th>Ceiling Ht.</th><th>Notes</th></tr></thead>
            <tbody>${roomSchedule.map(r => `<tr><td><strong>${r.name}</strong></td><td>${r.area}</td><td>${r.dimensions}</td><td>${r.flooring}</td><td>${r.ceiling}</td><td>${r.notes}</td></tr>`).join('')}</tbody>
          </table>
        </div>
        <div class="section">
          <div class="section-title">Material List</div>
          <table>
            <thead><tr><th>Material</th><th>Category</th><th>Qty</th><th>Unit</th><th>Est. Cost (${sym})</th></tr></thead>
            <tbody>${materialList.map(m => `<tr><td><strong>${m.material}</strong></td><td>${m.category}</td><td>${m.quantity}</td><td>${m.unit}</td><td>${sym}${m.cost.toLocaleString()}</td></tr>`).join('')}</tbody>
          </table>
        </div>
        <div class="section">
          <div class="section-title">Cost Estimate Summary</div>
          ${costBreakdown.map(s => `
            <div class="cost-section">
              <div class="cost-section-title">${s.category}</div>
              ${s.items.map(i => `<div class="cost-row"><span>${i.name}</span><span>${sym}${i.cost.toLocaleString()}</span></div>`).join('')}
              <div class="cost-subtotal"><span>Subtotal</span><span>${sym}${s.items.reduce((a, i) => a + i.cost, 0).toLocaleString()}</span></div>
            </div>
          `).join('')}
          <div class="grand-total"><span>Grand Total</span><span>${sym}${totalCost.toLocaleString()}</span></div>
        </div>
        <div class="section">
          <div class="section-title">Specification Sheet</div>
          <div class="spec-grid">
            ${[
              ['Wall Thickness', '200mm (8")'],
              ['Insulation Type', 'Mineral Wool R-30'],
              ['Window Specs', 'Double Glazed, Low-E'],
              ['Door Specs', 'Solid Core, Fire Rated'],
              ['Foundation Type', 'Reinforced Concrete Slab'],
              ['Roof Type', 'Pitched, Clay Tiles'],
            ].map(([label, value]) => `<div class="spec-item"><div class="spec-label">${label}</div><div class="spec-value">${value}</div></div>`).join('')}
          </div>
        </div>
        <div class="footer">
          <span>Master Architect · Construction Documents</span>
          <span>Confidential – For internal use only</span>
        </div>
      </body>
      </html>
    `;
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) return;
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 400);
  };

  // Auto-generate room schedule from config
  const roomSchedule = [
    { name: 'Master Bedroom', area: 18.5, dimensions: '4.5m × 4.1m', flooring: 'Hardwood', ceiling: '2.7m', notes: 'En-suite bathroom' },
    ...Array.from({ length: floorPlanConfig.bedrooms - 1 }, (_, i) => ({
      name: `Bedroom ${i + 2}`,
      area: 12.3,
      dimensions: '3.5m × 3.5m',
      flooring: 'Hardwood',
      ceiling: '2.7m',
      notes: 'Standard'
    })),
    { name: 'Living Room', area: 28.4, dimensions: '7.1m × 4.0m', flooring: 'Hardwood', ceiling: '3.0m', notes: 'Open concept' },
    { name: 'Kitchen', area: isOpenKitchen ? 22.1 : 15.8, dimensions: isOpenKitchen ? '6.5m × 3.4m' : '4.5m × 3.5m', flooring: 'Tile', ceiling: '2.7m', notes: isOpenKitchen ? 'Open to living' : 'Closed layout' },
    { name: 'Dining Room', area: 14.2, dimensions: '4.0m × 3.5m', flooring: 'Hardwood', ceiling: '2.7m', notes: 'Adjacent to kitchen' },
    ...Array.from({ length: floorPlanConfig.bathrooms }, (_, i) => ({
      name: i === 0 ? 'Master Bathroom' : `Bathroom ${i + 1}`,
      area: i === 0 ? 8.5 : 5.2,
      dimensions: i === 0 ? '3.0m × 2.8m' : '2.3m × 2.3m',
      flooring: 'Tile',
      ceiling: '2.7m',
      notes: i === 0 ? 'Double vanity' : 'Standard'
    })),
    ...(floorPlanConfig.extras.includes('garage') ? [{ name: 'Garage', area: 36.0, dimensions: '6.0m × 6.0m', flooring: 'Concrete', ceiling: '2.4m', notes: 'Double car' }] : []),
    ...(floorPlanConfig.extras.includes('pool') ? [{ name: 'Pool Area', area: 45.0, dimensions: '10.0m × 4.5m', flooring: 'Deck', ceiling: 'Open', notes: 'Outdoor' }] : []),
    ...(floorPlanConfig.extras.includes('gym') ? [{ name: 'Home Gym', area: 16.5, dimensions: '4.5m × 3.7m', flooring: 'Rubber', ceiling: '2.7m', notes: 'Equipment ready' }] : [])
  ];

  const materialList = [
    { material: 'Concrete Foundation', category: 'Structural', quantity: Math.round(floorPlanConfig.area * 0.15), unit: 'm³', cost: 12500 },
    { material: 'Steel Reinforcement', category: 'Structural', quantity: Math.round(floorPlanConfig.area * 2.5), unit: 'kg', cost: 8400 },
    { material: 'Brick Masonry', category: 'Structural', quantity: Math.round(floorPlanConfig.area * 120), unit: 'units', cost: 15600 },
    { material: 'Roof Trusses', category: 'Structural', quantity: Math.round(floorPlanConfig.area * 0.8), unit: 'm²', cost: 9800 },
    { material: 'Roofing Tiles', category: 'Exterior', quantity: Math.round(floorPlanConfig.area * 1.2), unit: 'm²', cost: 11200 },
    { material: 'Exterior Paint', category: 'Finish', quantity: Math.round(floorPlanConfig.area * 0.4), unit: 'L', cost: 3200 },
    { material: 'Windows (Double Glazed)', category: 'Exterior', quantity: 18, unit: 'units', cost: 14400 },
    { material: 'Entry Doors', category: 'Exterior', quantity: 2, unit: 'units', cost: 3600 },
    { material: 'Hardwood Flooring', category: 'Finish', quantity: Math.round(floorPlanConfig.area * 0.6), unit: 'm²', cost: 18900 },
    { material: 'Ceramic Tiles', category: 'Finish', quantity: Math.round(floorPlanConfig.area * 0.25), unit: 'm²', cost: 6800 },
    { material: 'Interior Doors', category: 'Finish', quantity: 8 + floorPlanConfig.bedrooms, unit: 'units', cost: 4200 },
    { material: 'Kitchen Cabinets', category: 'Finish', quantity: 12, unit: 'units', cost: 9600 },
    { material: 'Bathroom Fixtures', category: 'Finish', quantity: floorPlanConfig.bathrooms * 4, unit: 'units', cost: 7200 },
    { material: 'Electrical Wiring', category: 'MEP', quantity: Math.round(floorPlanConfig.area * 1.5), unit: 'm', cost: 8900 },
    { material: 'Plumbing Pipes', category: 'MEP', quantity: Math.round(floorPlanConfig.area * 1.2), unit: 'm', cost: 6700 },
    { material: 'HVAC System', category: 'MEP', quantity: 1, unit: 'system', cost: 12500 },
    { material: 'Insulation', category: 'Finish', quantity: Math.round(floorPlanConfig.area * 1.5), unit: 'm²', cost: 5400 },
    { material: 'Drywall', category: 'Finish', quantity: Math.round(floorPlanConfig.area * 2.8), unit: 'm²', cost: 8900 }
  ];

  const costBreakdown = [
    { category: 'Foundation', items: [
      { name: 'Excavation & Site Prep', cost: 8500 },
      { name: 'Concrete Foundation', cost: 12500 },
      { name: 'Waterproofing', cost: 3200 }
    ]},
    { category: 'Structure', items: [
      { name: 'Steel Reinforcement', cost: 8400 },
      { name: 'Brick Masonry', cost: 15600 },
      { name: 'Roof Trusses', cost: 9800 },
      { name: 'Roofing', cost: 11200 }
    ]},
    { category: 'Exterior', items: [
      { name: 'Windows & Doors', cost: 18000 },
      { name: 'Exterior Cladding', cost: 9600 },
      { name: 'Paint & Finish', cost: 3200 }
    ]},
    { category: 'Interior Finishes', items: [
      { name: 'Flooring', cost: 25700 },
      { name: 'Interior Doors', cost: 4200 },
      { name: 'Kitchen Cabinets', cost: 9600 },
      { name: 'Bathroom Fixtures', cost: 7200 },
      { name: 'Drywall & Paint', cost: 12400 }
    ]},
    { category: 'MEP Systems', items: [
      { name: 'Electrical', cost: 8900 },
      { name: 'Plumbing', cost: 6700 },
      { name: 'HVAC', cost: 12500 },
      { name: 'Insulation', cost: 5400 }
    ]},
    { category: 'Landscaping', items: [
      { name: 'Lawn & Garden', cost: 4500 },
      { name: 'Driveway', cost: 6800 },
      { name: 'Outdoor Lighting', cost: 2100 }
    ]}
  ];

  const totalCost = costBreakdown.reduce((sum, section) =>
    sum + section.items.reduce((s, item) => s + item.cost, 0), 0
  );

  const getBudgetTier = (cost: number) => {
    if (cost < 100000) return { label: 'Starter', color: 'bg-green-500', width: '25%' };
    if (cost < 200000) return { label: 'Mid-Range', color: 'bg-teal-500', width: '50%' };
    if (cost < 350000) return { label: 'Premium', color: 'bg-orange-500', width: '75%' };
    return { label: 'Luxury', color: 'bg-amber-500', width: '100%' };
  };

  const budgetTier = getBudgetTier(totalCost);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Construction Documents</h3>
          <p className="text-sm text-gray-500 mt-1">Auto-generated schedules, materials &amp; cost estimates</p>
        </div>
        <div className="flex items-center gap-3">
          {isGenerated && (
            <>
              <button
                onClick={handleShare}
                disabled={isSharing}
                className="px-4 py-2 bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isSharing ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-share-line"></i>}
                Share
              </button>
              <button
                onClick={handleRecalculate}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-refresh-line"></i>Recalculate
              </button>
            </>
          )}
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="px-5 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white rounded-lg text-sm font-medium transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 cursor-pointer"
          >
            <i className="ri-file-list-3-line"></i>
            {isGenerating ? 'Generating...' : 'Generate Documents'}
          </button>
        </div>
      </div>

      {/* Generation Progress */}
      {isGenerating && (
        <div className="mb-6 p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border border-teal-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center">
              <i className="ri-loader-4-line text-white animate-spin"></i>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{generationSteps[generationStep]}</p>
              <p className="text-xs text-gray-500 mt-0.5">Step {generationStep + 1} of {generationSteps.length}</p>
            </div>
          </div>
          <div className="w-full bg-white rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-teal-500 to-cyan-600 transition-all duration-500"
              style={{ width: `${((generationStep + 1) / generationSteps.length) * 100}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Content */}
      {isGenerated && (
        <div className="flex-1 overflow-y-auto space-y-6">
          {/* Room Schedule */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <i className="ri-layout-grid-line text-teal-600"></i>Room Schedule
              </h4>
              <span className="text-xs text-gray-500">{roomSchedule.length} rooms</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Room Name</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Area (m²)</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Dimensions</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Flooring</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Ceiling Height</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {roomSchedule.map((room, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-2.5 font-medium text-gray-900">{room.name}</td>
                      <td className="px-4 py-2.5 text-gray-700">{room.area}</td>
                      <td className="px-4 py-2.5 text-gray-700">{room.dimensions}</td>
                      <td className="px-4 py-2.5 text-gray-700">{room.flooring}</td>
                      <td className="px-4 py-2.5 text-gray-700">{room.ceiling}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{room.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Material List */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <i className="ri-stack-line text-teal-600"></i>Material List
              </h4>
              <span className="text-xs text-gray-500">{materialList.length} items</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Material</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Category</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Quantity</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Unit</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Estimated Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {materialList.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-2.5 font-medium text-gray-900">{item.material}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                          item.category === 'Structural' ? 'bg-teal-100 text-teal-700' :
                          item.category === 'Finish' ? 'bg-orange-100 text-orange-700' :
                          item.category === 'MEP' ? 'bg-amber-100 text-amber-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {item.category}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-700">{item.quantity}</td>
                      <td className="px-4 py-2.5 text-gray-700">{item.unit}</td>
                      <td className="px-4 py-2.5 text-right font-medium text-gray-900">{sym}{item.cost.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cost Estimate */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <i className="ri-money-dollar-circle-line text-teal-600"></i>Cost Estimate
              </h4>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Currency:</span>
                <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1">
                  {(['USD', 'EUR', 'GBP'] as const).map((curr) => (
                    <button
                      key={curr}
                      onClick={() => setCurrency(curr)}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        currency === curr ? 'bg-teal-500 text-white' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {curr}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {costBreakdown.map((section, idx) => (
                <div key={idx}>
                  <h5 className="text-sm font-semibold text-gray-900 mb-2">{section.category}</h5>
                  <div className="space-y-1.5 mb-2">
                    {section.items.map((item, itemIdx) => (
                      <div key={itemIdx} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{item.name}</span>
                        <span className="font-medium text-gray-900">{sym}{item.cost.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200">
                    <span className="font-semibold text-gray-900">Subtotal</span>
                    <span className="font-semibold text-gray-900">
                      {sym}{section.items.reduce((s, i) => s + i.cost, 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
              <div className="pt-4 border-t-2 border-gray-300">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-base font-bold text-gray-900">Grand Total</span>
                  <span className="text-xl font-bold text-teal-600">{sym}{totalCost.toLocaleString()}</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-600">Budget Tier</span>
                    <span className="text-xs font-semibold text-gray-900">{budgetTier.label}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div className={`h-full ${budgetTier.color} transition-all duration-500`} style={{ width: budgetTier.width }}></div>
                  </div>
                  <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                    <span>Starter</span><span>Mid-Range</span><span>Premium</span><span>Luxury</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Spec Sheet */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <i className="ri-file-text-line text-teal-600"></i>Specification Sheet
              </h4>
            </div>
            <div className="p-4 grid grid-cols-2 gap-4">
              {[
                ['Wall Thickness', '200mm (8")'],
                ['Insulation Type', 'Mineral Wool R-30'],
                ['Window Specs', 'Double Glazed, Low-E'],
                ['Door Specs', 'Solid Core, Fire Rated'],
                ['Foundation Type', 'Reinforced Concrete Slab'],
                ['Roof Type', 'Pitched, Clay Tiles'],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs text-gray-500 mb-1">{label}</p>
                  <p className="text-sm font-medium text-gray-900">{value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Export Options */}
          <div className="flex items-center gap-3 pb-6 mt-2">
            <button
              onClick={handleExportPDF}
              disabled={isExporting}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
            >
              {isExporting ? <i className="ri-loader-4-line animate-spin text-lg"></i> : <i className="ri-file-pdf-line text-lg"></i>}
              {isExporting ? 'Generating PDF...' : 'Download PDF'}
            </button>
            <button onClick={handlePrint} className="flex-1 px-4 py-3 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer">
              <i className="ri-printer-line text-lg"></i>Print
            </button>
            <button onClick={handleShare} disabled={isSharing} className="flex-1 px-4 py-3 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60 cursor-pointer">
              {isSharing ? <i className="ri-loader-4-line animate-spin text-lg"></i> : <i className="ri-share-line text-lg"></i>}
              Share
            </button>
            <button onClick={() => handleExport('Excel File')} className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer">
              <i className="ri-file-excel-line text-lg"></i>Excel
            </button>
            <button onClick={() => handleExport('Revit File')} className="flex-1 px-4 py-3 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer">
              <i className="ri-building-line text-lg"></i>Revit
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isGenerating && !isGenerated && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-list-3-line text-4xl text-teal-600"></i>
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">No Documents Generated</h4>
            <p className="text-sm text-gray-500 mb-6">
              Click &quot;Generate Documents&quot; to auto-create room schedules, material lists, and cost estimates based on your floor plan configuration.
            </p>
            <button onClick={handleGenerate} className="px-6 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white rounded-lg text-sm font-medium transition-all whitespace-nowrap inline-flex items-center gap-2 cursor-pointer">
              <i className="ri-file-list-3-line"></i>Generate Documents
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      {showToast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 transition-all ${
          toastType === 'info' ? 'bg-teal-600 text-white' : 'bg-gray-900 text-white'
        }`}>
          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${toastType === 'info' ? 'bg-white/20' : 'bg-green-500'}`}>
            <i className={`text-sm ${toastType === 'info' ? 'ri-information-line' : 'ri-check-line'}`}></i>
          </div>
          <span className="text-sm font-medium">{toastMessage}</span>
        </div>
      )}
    </div>
  );
}